import React, { Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import i18n from "./i18n";

// Import validators (Yup extensions)
import './validators';

// Import scss
import "./assets/scss/theme.scss";
import "./assets/scss/custom.scss";

// Import css
import "react-toastify/dist/ReactToastify.css";
import "simplebar-react/dist/simplebar.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "./App.css";

import { AuthUserInfo, IsLockScreen, RouteTitles } from "./constants";
import { updateAppTitle } from "./helpers";
import LocalstorageService from "./helpers/localstorage-service";

import { changeRTLLayout } from "./store/slices/Layout/layoutSlice";
import { setAuthToken, setAuthUser } from "./store/slices/Auth/authSlice";

// Layouts — נטענים סינכרוני (קריטיים לכל דף)
import NonAuthLayout from "./components/Layout/NonAuthLayout";
import VerticalLayout from "./components/Layout/VerticalLayout";
import SysAdminLayout from "./components/Layout/SysAdminLayout";

// Auth — נטענים סינכרוני (דפים ראשונים)
import RouteAuthGuard from "./components/RouteAuthGuard";
import Login from "./pages/Authentication/Login";

// רכיב טעינה לזמן שה-chunk נטען
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">טוען...</span>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Lazy imports — כל דף נטען רק כשניגשים אליו (code-splitting)
// ═══════════════════════════════════════════════════════════════

// Auth
const ForgotPassword = React.lazy(() => import("./pages/Authentication/ForgotPasword"));
const ResetPassword = React.lazy(() => import("./pages/Authentication/ResetPasword"));
const VerifyEmail = React.lazy(() => import("./pages/Authentication/VerifyEmail"));
const Lockscreen = React.lazy(() => import("./pages/Authentication/Lockscreen"));

// Account
const UserProfile = React.lazy(() => import("./pages/Account/UserProfile"));

// Dashboard
const Dashboard = React.lazy(() => import("./pages/Dashboard"));

// Bank
const Bank = React.lazy(() => import("./pages/Bank"));
const BankList = React.lazy(() => import("./pages/Bank/List"));
const AddBank = React.lazy(() => import("./pages/Bank/Add"));
const UpdateBank = React.lazy(() => import("./pages/Bank/Update"));
const BankBranch = React.lazy(() => import("./pages/Bank/Branch"));

// Company
const Company = React.lazy(() => import("./pages/Company"));

// Admin
const SpecialDaysConfig = React.lazy(() => import("./pages/Admin/SpecialDaysConfig"));
const MessageRoleMapping = React.lazy(() => import("./pages/Admin/MessageRoleMapping"));
const ShabbatSettings = React.lazy(() => import("./pages/Admin/ShabbatSettings"));
const UserNotificationPreferences = React.lazy(() => import("./pages/Admin/UserNotificationPreferences"));
const SendMessage = React.lazy(() => import("./pages/Admin/SendMessage"));
const CompanyParameters = React.lazy(() => import("./pages/Admin/CompanyParameters"));
const TerminalOperationCorrectionPage = React.lazy(() => import("./pages/Admin/TerminalOperationCorrectionPage"));

// Agent
const Agent = React.lazy(() => import("./pages/Agent"));
const AgentList = React.lazy(() => import("./pages/Agent/List"));
const EditAgent = React.lazy(() => import("./pages/Agent/EditAgent"));

// Agent Activity
const AgentActivity = React.lazy(() => import("./pages/AgentActivity"));
const AgentBankWithdrawal = React.lazy(() => import("./pages/AgentActivity/BankWithdrawal"));
const AgentSplitMoney = React.lazy(() => import("./pages/AgentActivity/SplitMoney"));

// Employee
const Employee = React.lazy(() => import("./pages/Employee"));
const EmployeeList = React.lazy(() => import("./pages/Employee/List"));
const AddEmployee = React.lazy(() => import("./pages/Employee/Add"));
const EditEmployee = React.lazy(() => import("./pages/Employee/EditEmployee"));

// Employee Activity
const EmployeeActivity = React.lazy(() => import("./pages/EmployeeActivity"));
const EmpBankWithdrawal = React.lazy(() => import("./pages/EmployeeActivity/BankWithdrawal"));
const ChargingTerminal = React.lazy(() => import("./pages/EmployeeActivity/ChargingTerminal"));
const EmpSplitMoney = React.lazy(() => import("./pages/EmployeeActivity/SplitMoney"));
const WithdrawalTerminal = React.lazy(() => import("./pages/EmployeeActivity/WithdrawalTerminal"));
const ListEmployeeTerminals = React.lazy(() => import("./pages/EmployeeActivity/ListEmployeeTerminals"));
const EmployeeLoadsList = React.lazy(() => import("./pages/EmployeeActivity/EmployeeLoadsList"));
const AlternativeLoadingTerminals = React.lazy(() => import("./pages/EmployeeActivity/AlternativeLoadingTerminals"));

// Locations
const Location = React.lazy(() => import("./pages/Location"));
const LocationList = React.lazy(() => import("./pages/Location/List"));
const AddLocation = React.lazy(() => import("./pages/Location/Add"));
const EditLocation = React.lazy(() => import("./pages/Location/EditLocation"));

// Machines/Terminals
const Machines = React.lazy(() => import("./pages/Machines"));
const MachinesList = React.lazy(() => import("./pages/Machines/List"));
const AddMachine = React.lazy(() => import("./pages/Machines/Add"));
const UpdateMachine = React.lazy(() => import("./pages/Machines/Update"));
const TerminalPage = React.lazy(() => import("./pages/Machines/TerminalPage"));

// Users
const Users = React.lazy(() => import("./pages/Account/Users"));
const UserList = React.lazy(() => import("./pages/Account/Users/List"));
const AddUser = React.lazy(() => import("./pages/Account/Users/Add"));
const UpdateUser = React.lazy(() => import("./pages/Account/Users/Update"));

// Unknown Route
const Error404 = React.lazy(() => import("./pages/Utility/Error404"));

// Cash Expense
const CashExpense = React.lazy(() => import("./pages/CashExpense"));

// Special Days
const SpecialDays = React.lazy(() => import("./pages/SpecialDays"));
const SpecialDaysList = React.lazy(() => import("./pages/SpecialDays/List"));
const AddSpecialDays = React.lazy(() => import("./pages/SpecialDays/Add"));
const UpdateSpecialDays = React.lazy(() => import("./pages/SpecialDays/Update"));

// Profitability — דפים כבדים (xlsx, jspdf)
const ProfitabilityPage = React.lazy(() => import("./pages/Profitability/ProfitabilityPage"));
const MonthlyReport = React.lazy(() => import("./pages/Profitability/MonthlyReport"));
const EmployeeSalary = React.lazy(() => import("./pages/Profitability/EmployeeSalary"));
const MonthlyExpenses = React.lazy(() => import("./pages/Profitability/MonthlyExpenses"));
const InvoiceTracking = React.lazy(() => import("./pages/Profitability/InvoiceTracking"));
const InvoiceScan = React.lazy(() => import("./pages/Profitability/InvoiceScan"));
const PaymentSchedule = React.lazy(() => import("./pages/Profitability/PaymentSchedule"));
const LocationGroups = React.lazy(() => import("./pages/Profitability/LocationGroups"));

// Reports — דפים כבדים (xlsx, jspdf, charts)
const ReportPage = React.lazy(() => import("./pages/Report/ReportPage"));
const AgentCashExpense = React.lazy(() => import("./pages/Report/AgentCashExpense"));
const ReferenceReport = React.lazy(() => import("./pages/Report/ReferenceReport"));
const TerminalReport = React.lazy(() => import("./pages/Report/TerminalReport"));
const WithdrawalReport = React.lazy(() => import("./pages/Report/WithdrawalReport"));
const LoadingGapReport = React.lazy(() => import("./pages/Report/LoadingGapReport"));
const EmployeeLoadingGapsCumulativeReport = React.lazy(() => import("./pages/Report/EmployeeLoadingGapsCumulativeReport"));
const CheckFlowReport = React.lazy(() => import("./pages/Report/CheckFlowReport"));
const EmployeeBalanceReport = React.lazy(() => import("./pages/Report/EmployeeBalanceReport"));
const BatchLinesReconciliationReport = React.lazy(() => import("./pages/Report/BatchLinesReconciliationReport"));
const MissingInvoiceBatchesReport = React.lazy(() => import("./pages/Report/MissingInvoiceBatchesReport"));

// SysAdmin
const SysAdminDashboard = React.lazy(() => import("./pages/SysAdmin/Dashboard"));
const CompanySetup = React.lazy(() => import("./pages/SysAdmin/CompanySetup"));
const BillingReport = React.lazy(() => import("./pages/SysAdmin/BillingReport"));
const CalendarPage = React.lazy(() => import("./pages/SysAdmin/CalendarPage"));
const RecognitionPresets   = React.lazy(() => import("./pages/SysAdmin/RecognitionPresets"));
const VatPolicyModes       = React.lazy(() => import("./pages/SysAdmin/VatPolicyModes"));
const CompanySetupTemplate = React.lazy(() => import("./pages/SysAdmin/CompanySetupTemplate"));

function App() {
  const dispatch = useDispatch();
  const location = useLocation();

  const [isRTLMode, setIsRTLMode] = useState(false);

  const userInfo = LocalstorageService.getItem(AuthUserInfo) || null;

  // Set direction for LTR/RTL directions.
  useEffect(() => {
    userInfo?.data && dispatch(setAuthUser(userInfo?.data));
    userInfo?.token && dispatch(setAuthToken(userInfo?.token));
    const dir = ["he"].includes(i18n.resolvedLanguage) ? "rtl" : "ltr";
    const htmlElem = document.getElementsByTagName("html");
    const bodyElem = document.getElementsByTagName("body");

    if (htmlElem && bodyElem && dir === "rtl") {
      htmlElem[0].setAttribute("dir", "rtl");
      bodyElem[0].style.textAlign = "right";
      bodyElem[0].classList.add("rtl-mode");
      setIsRTLMode(true);
    } else {
      htmlElem[0].setAttribute("dir", "");
      bodyElem[0].style.textAlign = "";
      bodyElem[0].classList.remove("rtl-mode");
      setIsRTLMode(false);
    }

    dispatch(changeRTLLayout({ isRtlLayout: dir === "rtl" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update document title
  useEffect(() => {
    const matchPath = [
      "/account/users/",
      "/verify-email/",
      "/terminal/",
      "/bank/",
      "/company/",
      "/agent/",
      "/employee/",
      "/locations/",
      "/special-days/",
      "/loading-terminal/",
      "/cash-expense/",
      "/report/",
      "/sysadmin/",
    ];

    let routePath = location.pathname;

    const idx = matchPath.findIndex((v) => routePath.startsWith(v));
    if (idx !== -1) routePath = matchPath[idx] + "*";
    updateAppTitle(RouteTitles[routePath] || "404");
  }, [location]);

  return (
    <React.Fragment>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/"
            element={
              <RouteAuthGuard
                isAuthProtected={true}
                isLockScreen={!!LocalstorageService.getItem(IsLockScreen)}
              >
                <VerticalLayout />
              </RouteAuthGuard>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />

            <Route path="/bank" element={<Bank />}>
              <Route path="list" element={<BankList />} />
              <Route path="add" element={<AddBank />} />
              <Route path="update/:bankId" element={<UpdateBank />} />

              <Route path=":bankId/branches" element={<BankBranch />} />
            </Route>

            <Route path="/company/list" element={<Company />} />

            {/* Admin Routes */}
            <Route path="/admin/special-days-config" element={<SpecialDaysConfig />} />
            <Route path="/admin/message-role-mapping" element={<MessageRoleMapping />} />
            <Route path="/admin/notification-preferences" element={<UserNotificationPreferences />} />
            <Route path="/admin/shabbat-settings" element={<ShabbatSettings />} />
            <Route path="/admin/company-parameters" element={<CompanyParameters />} />
            <Route path="/admin/terminal-correction" element={<TerminalOperationCorrectionPage />} />
            <Route path="/admin/send-message" element={<SendMessage />} />

            <Route path="/agent" element={<Agent />}>
              <Route path="list" element={<AgentList />} />
              <Route path="update/:agentId" element={<EditAgent />} />
            </Route>

            <Route path="/agent-activity" element={<AgentActivity />}>
              <Route path="bank-withdrawal" element={<AgentBankWithdrawal />} />
              <Route path="split-money" element={<AgentSplitMoney />} />
            </Route>

            <Route path="/employee" element={<Employee />}>
              <Route path="list" element={<EmployeeList />} />
              <Route path="add" element={<AddEmployee />} />
              <Route path="update/:employeeId" element={<EditEmployee />} />
            </Route>

            <Route path="/employee-activity" element={<EmployeeActivity />}>
              <Route path="bank-withdrawal" element={<EmpBankWithdrawal />} />
              <Route path="charging-terminal" element={<ChargingTerminal />} />
              <Route path="split-money" element={<EmpSplitMoney />} />
              <Route path="withdrawal-terminal" element={<WithdrawalTerminal />} />
            </Route>

            <Route path="/locations" element={<Location />}>
              <Route path="list" element={<LocationList />} />
              <Route path="add" element={<AddLocation />} />
              <Route path="update/:locationId" element={<EditLocation />} />
            </Route>

            <Route path="/terminal" element={<Machines />}>
              <Route path="list" element={<MachinesList />} />
              <Route path="add" element={<AddMachine />} />
              <Route path="update/:machineId" element={<UpdateMachine />} />
            </Route>

            <Route path="/loading-terminal" element={<TerminalPage />}>
              <Route path="list" element={<ListEmployeeTerminals />} />
              <Route path="terminal-loads" element={<EmployeeLoadsList />} />
              <Route path="alternative" element={<AlternativeLoadingTerminals />} />
            </Route>

            <Route path="/cash-expense/list" element={<CashExpense />} />

            <Route path="/profitability" element={<ProfitabilityPage />}>
              <Route path="monthly-report" element={<MonthlyReport />} />
              <Route path="salary" element={<EmployeeSalary />} />
              <Route path="payment-schedule" element={<PaymentSchedule />} />
              <Route path="expenses" element={<MonthlyExpenses />} />
              <Route path="invoices" element={<InvoiceTracking />} />
              <Route path="invoice-scan" element={<InvoiceScan />} />
              <Route path="location-groups" element={<LocationGroups />} />
            </Route>

            <Route path="/report" element={<ReportPage />}>
              <Route path="agent-cash-expense" element={<AgentCashExpense />} />
              <Route path="reference" element={<ReferenceReport />} />
              <Route path="terminal" element={<TerminalReport />} />
              <Route path="withdrawal-count" element={<WithdrawalReport />} />
              <Route path="loading-gap"        element={<LoadingGapReport />} />
              <Route path="employee-loading-gaps-cumulative" element={<EmployeeLoadingGapsCumulativeReport />} />
              <Route path="check-flow"          element={<CheckFlowReport />} />
              <Route path="employee-balance"    element={<EmployeeBalanceReport />} />
              <Route path="batch-lines-reconciliation" element={<BatchLinesReconciliationReport />} />
              <Route path="missing-invoice-batches" element={<MissingInvoiceBatchesReport />} />
            </Route>

            <Route path="/special-days" element={<SpecialDays />}>
              <Route path="list" element={<SpecialDaysList />} />
              <Route path="add" element={<AddSpecialDays />} />
              <Route path="update/:dayId" element={<UpdateSpecialDays />} />
            </Route>

            <Route path="/account/profile" element={<UserProfile />} />

            <Route path="/account/users" element={<Users />}>
              <Route path="list" element={<UserList />} />
              <Route path="add" element={<AddUser />} />
              <Route path="update/:userId" element={<UpdateUser />} />
            </Route>
          </Route>

          {/* SysAdmin Routes */}
          <Route
            path="/sysadmin"
            element={
              <RouteAuthGuard isAuthProtected={true}>
                <SysAdminLayout />
              </RouteAuthGuard>
            }
          >
            <Route path="dashboard" element={<SysAdminDashboard />} />
            <Route path="company/create" element={<CompanySetup />} />
            <Route path="company/:companyId" element={<CompanySetup />} />
            <Route path="companies" element={<Company />} />
            <Route path="special-days-config" element={<SpecialDaysConfig />} />
            <Route path="billing" element={<BillingReport />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="recognition-presets"    element={<RecognitionPresets />} />
            <Route path="vat-policy-modes"        element={<VatPolicyModes />} />
            <Route path="company-setup-template"  element={<CompanySetupTemplate />} />
            <Route path="bank" element={<Bank />}>
              <Route path="list" element={<BankList />} />
              <Route path="add" element={<AddBank />} />
              <Route path="update/:bankId" element={<UpdateBank />} />
              <Route path=":bankId/branches" element={<BankBranch />} />
            </Route>
          </Route>

          <Route
            path="/login"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <Login />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/lock-screen"
            element={
              <RouteAuthGuard isAuthProtected={true}>
                <NonAuthLayout>
                  <Lockscreen />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <ForgotPassword />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/reset-password"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <ResetPassword />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/verify-email/:userId"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <VerifyEmail />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="*"
            element={
              <NonAuthLayout>
                <Error404 />
              </NonAuthLayout>
            }
          />
        </Routes>
      </Suspense>

      <ToastContainer
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        theme="colored"
        pauseOnFocusLoss={false}
        rtl={isRTLMode}
      />
    </React.Fragment>
  );
}

export default App;
